import { Injectable } from '@angular/core';
import Dexie from 'dexie';
import { Trip } from '../entities/trip';
import { DriverDto, GeneticMaterialDto, NurseryLocationDto, TrayTypeDto, TripDto, TruckDto } from '../../proxy/dto';
import { JourneyStatus, TripStatus } from '../../../app/proxy/enums';
import { NurseryLocation, NurseryLocationProperties } from '../entities/nursery-location';
import { GeneticMaterial, GeneticMaterialProperties } from '../entities/generic-material';
import { TrayType } from '../entities/tray-type';
import { Truck } from '../entities/truck';
import { TransportVendor } from '../entities/transport-vendor';
import { ShippingReportMaterialDto, ShippingReportQualityDto, TransportVendorDto, TripEventDto, TripJourneyRouteDto, UserDto } from '../../proxy/integrations/seedling-supply-service/dto';
import { Driver } from '../entities/driver';
import { ShippingReportMaterial } from '../entities/shipping-report-material';
import { ShippingReportQuality } from '../entities/shipping-report-quality';
import { TripEvent } from '../entities/trip-event';
import { SynchronizationSession } from '../entities/synchronization-session';
import { Synchronization } from '../entities/synchronization';
import { TripJourneyRoute } from '../entities/trip-journey-route';
import { User } from '../entities/user';
import { AppConsts } from '../AppConsts';

@Injectable({
  providedIn: 'root'
})
export class LocalDbService extends Dexie {

  tripsTable!: Dexie.Table<Trip, string>;
  nurseryLocationsTable!: Dexie.Table<NurseryLocation, string>;
  geneticMaterialsTable!: Dexie.Table<GeneticMaterial, string>;
  trayTypesTable!: Dexie.Table<TrayType, number>;
  trucksTable!: Dexie.Table<Truck, string>;
  transportVendorsTable!: Dexie.Table<TransportVendor, number>;
  driversTable!: Dexie.Table<Driver, string>;
  shippingReportMaterialTable!: Dexie.Table<ShippingReportMaterial, string>;
  shippingReportQualitiesTable!: Dexie.Table<ShippingReportQuality, string>;
  tripEventsTable!: Dexie.Table<TripEvent, string>;
  synchronizationSessionsTable!: Dexie.Table<SynchronizationSession, string>;
  synchronizationTable!: Dexie.Table<Synchronization, string>;
  tripJourneyRouteTable!: Dexie.Table<TripJourneyRoute, string>;
  userTable!: Dexie.Table<User, number>;

  constructor() {
    super('MyLocalDb');

    this.version(1).stores({
      tripsTable: 'tripNumber',
      nurseryLocationsTable: 'id, vendorId',
      geneticMaterialsTable: 'id, geneticMaterialId',
      trayTypesTable: 'trayTypeId',
      trucksTable: 'id',
      transportVendorsTable: 'vendorId',
      driversTable: 'driverId',
      shippingReportMaterialTable: 'id, dispatchNumber',
      shippingReportQualitiesTable: 'id, dispatchNumber',
      tripEventsTable: '++id, tripId, journeyId, [tripId+journeyId]',
      synchronizationSessionsTable: 'synchronizationId, lastSync',
      synchronizationTable: 'synchronizationId, itemType',
      tripJourneyRouteTable: '++id, [tripNumber+destinationId], tripNumber, destinationId',
      userTable: 'userId',
    });

    this.version(2).stores({
      tripsTable: 'tripNumber',
      nurseryLocationsTable: 'id, vendorId, nameSearch, [vendorId+nameSearch]',
      geneticMaterialsTable: 'id, geneticMaterialId',
      trayTypesTable: 'trayTypeId',
      trucksTable: 'id',
      transportVendorsTable: 'vendorId',
      driversTable: 'driverId',
      shippingReportMaterialTable: 'id, dispatchNumber',
      shippingReportQualitiesTable: 'id, dispatchNumber',
      tripEventsTable: '++id, tripId, journeyId, [tripId+journeyId]',
      synchronizationSessionsTable: 'synchronizationId, lastSync',
      synchronizationTable: 'synchronizationId, itemType',
      tripJourneyRouteTable: '++id, [tripNumber+destinationId], tripNumber, destinationId',
      userTable: 'userId',
    }).upgrade(transaction => transaction.table('nurseryLocationsTable').toCollection().modify(location => {
      location.nameSearch = location.name?.toLocaleLowerCase();
    }));

    this.version(3).stores({
      tripsTable: 'tripNumber',
      nurseryLocationsTable: 'id, vendorId, normalizedName, [vendorId+normalizedName]',
      geneticMaterialsTable: 'id, geneticMaterialId',
      trayTypesTable: 'trayTypeId',
      trucksTable: 'id',
      transportVendorsTable: 'vendorId',
      driversTable: 'driverId',
      shippingReportMaterialTable: 'id, dispatchNumber',
      shippingReportQualitiesTable: 'id, dispatchNumber',
      tripEventsTable: '++id, tripId, journeyId, [tripId+journeyId]',
      synchronizationSessionsTable: 'synchronizationId, lastSync',
      synchronizationTable: 'synchronizationId, itemType',
      tripJourneyRouteTable: '++id, [tripNumber+destinationId], tripNumber, destinationId',
      userTable: 'userId',
    }).upgrade(transaction => transaction.table('nurseryLocationsTable').toCollection().modify(location => {
      location.normalizedName = location.name?.toLocaleLowerCase();
      delete location.nameSearch;
    }));

    this.tripsTable = this.table('tripsTable');
    this.nurseryLocationsTable = this.table('nurseryLocationsTable');
    this.geneticMaterialsTable = this.table('geneticMaterialsTable');
    this.trayTypesTable = this.table('trayTypesTable');
    this.trucksTable = this.table('trucksTable');
    this.transportVendorsTable = this.table('transportVendorsTable');
    this.driversTable = this.table('driversTable');
    this.shippingReportMaterialTable = this.table('shippingReportMaterialTable');
    this.shippingReportQualitiesTable = this.table('shippingReportQualitiesTable');
    this.tripEventsTable = this.table('tripEventsTable');
    this.synchronizationSessionsTable = this.table('synchronizationSessionsTable');
    this.synchronizationTable = this.table('synchronizationTable');
    this.tripJourneyRouteTable = this.table('tripJourneyRouteTable');
    this.userTable = this.table('userTable');
  }

  async addTrip(data: TripDto) {
    const trip = new Trip(data)
    return await this.tripsTable.put(trip);
  }

  async addTrips(dataList: TripDto[]) {
    const trips = dataList.map(dto => new Trip(dto));
    return await this.tripsTable.bulkPut(trips);
  }

  async clearTrips() {
    return await this.tripsTable.clear();
  }

  async clearLocations() {
    return await this.nurseryLocationsTable.clear();
  }

  async getTrips(locationId: number, onlyNotCompleted = false, completedJourneys = false): Promise<Trip[]> {
    const trips = await this.tripsTable
      .filter(trip => {
        if (onlyNotCompleted &&
          (trip.status === TripStatus.Finished || trip.status === TripStatus.Canceled)) {
          return false;
        }

        if (!Array.isArray(trip.journeys)) {
          return true;
        }

        const hasMatchingJourney = trip.journeys.some(j =>
          j.destinationId == locationId &&
          (completedJourneys
            ? j.status === JourneyStatus.Completed || j.status === JourneyStatus.Canceled
            : j.status !== JourneyStatus.Completed && j.status !== JourneyStatus.Canceled)
        );
        return hasMatchingJourney;
      })
      .toArray();

    return trips.map(dto => new Trip(dto));
  }

  async getTrip(tripNumber: string): Promise<Trip> {
    const trip = await this.tripsTable.get(tripNumber)
    return new Trip(trip);
  }

  async getPendingTrip(): Promise<Trip[]> {
    const allTrips = await this.tripsTable.toArray();
    const trips = allTrips.filter(t => t.isPublished === false);
    return trips.map(t => new Trip(t));
  }

  async getTripCount(): Promise<number> {
    return await this.tripsTable.count();
  }

  async addNurseryLocations(dataList: NurseryLocationDto[]) {
    const locations = dataList.map(location => ({
      ...location,
      normalizedName: location.name?.toLocaleLowerCase()
    }));

    return await this.nurseryLocationsTable.bulkAdd(locations);
  }

  getNurseryLocations(vendorId?: number): Promise<NurseryLocation[]> {
    if (vendorId && vendorId != AppConsts.ForestryVendor) {
      return this.nurseryLocationsTable
        .where(NurseryLocationProperties.vendorId)
        .equals(vendorId)
        .toArray();
    } else {
      return this.nurseryLocationsTable.toArray();
    }
  }

  getNurseryLocationsPage(vendorId: number | undefined, offset: number, limit: number): Promise<NurseryLocation[]> {
    if (vendorId && vendorId != AppConsts.ForestryVendor) {
      return this.nurseryLocationsTable.where(NurseryLocationProperties.vendorId).equals(vendorId).offset(offset).limit(limit).toArray();
    }

    return this.nurseryLocationsTable.toCollection().offset(offset).limit(limit).toArray();
  }

  getNurseryLocation(locationId: number, vendorId?: number): Promise<NurseryLocation | undefined> {
    const locations = vendorId && vendorId != AppConsts.ForestryVendor
      ? this.nurseryLocationsTable.where(NurseryLocationProperties.vendorId).equals(vendorId)
      : this.nurseryLocationsTable.toCollection();

    return locations.filter(location => location.locationId === locationId).first();
  }

  async getNurseryLocationsMatching(vendorId: number | undefined, searchTerm: string): Promise<NurseryLocation[]> {
    const normalizedSearchTerm = searchTerm.toLocaleLowerCase();

    if (vendorId && vendorId != AppConsts.ForestryVendor) {
      return this.nurseryLocationsTable
        .where('[vendorId+normalizedName]')
        .between([vendorId, normalizedSearchTerm], [vendorId, `${normalizedSearchTerm}\uffff`], true, true)
        .toArray();
    }

    return this.nurseryLocationsTable
      .where(NurseryLocationProperties.normalizedName)
      .startsWith(normalizedSearchTerm)
      .toArray();
  }

  getNurseryLocationsCount(vendorId?: number): Promise<number> {
    if (vendorId && vendorId != AppConsts.ForestryVendor) {
      return this.nurseryLocationsTable.where(NurseryLocationProperties.vendorId).equals(vendorId).count();
    }

    return this.nurseryLocationsTable.count();
  }

  async addGeneticMaterials(dataList: GeneticMaterialDto[]) {
    return await this.geneticMaterialsTable.bulkPut(dataList);
  }

  async clearGeneticMaterials() {
    return await this.geneticMaterialsTable.clear();
  }

  getGeneticMaterials(): Promise<GeneticMaterial[]> {
    return this.geneticMaterialsTable
      .toArray();
  }

  async getGeneticMaterial(geneticMaterialId: number): Promise<GeneticMaterial> {
    const geneticMaterial = await this.geneticMaterialsTable
      .where(GeneticMaterialProperties.geneticMaterialId)
      .equals(geneticMaterialId)
      .first();
    return geneticMaterial;
  }

  getGeneticMaterialCount(): Promise<number> {
    return this.geneticMaterialsTable.count();
  }

  async addTrayTypes(dataList: TrayTypeDto[]) {
    return await this.trayTypesTable.bulkPut(dataList);
  }

  async clearTrayTypes() {
    return await this.trayTypesTable.clear();
  }

  getTrayTypes(): Promise<TrayType[]> {
    return this.trayTypesTable
      .toArray();
  }

  getTrayTypesCount(): Promise<number> {
    return this.trayTypesTable.count();
  }

  async addTrucks(dataList: TruckDto[]) {
    return await this.trucksTable.bulkPut(dataList);
  }

  async clearTrucks() {
    return await this.trucksTable.clear();
  }

  getTrucks(): Promise<Truck[]> {
    return this.trucksTable
      .toArray();
  }

  getTrucksCount(): Promise<number> {
    return this.trucksTable.count();
  }

  async addTransportVendors(dataList: TransportVendorDto[]) {
    return await this.transportVendorsTable.bulkPut(dataList);
  }

  async clearTransportVendors() {
    return await this.transportVendorsTable.clear();
  }

  getTransportVendors(): Promise<TransportVendor[]> {
    return this.transportVendorsTable
      .toArray();
  }

  getTransportVendorsCount(): Promise<number> {
    return this.transportVendorsTable.count();
  }

  async addDrivers(dataList: DriverDto[]) {
    return await this.driversTable.bulkPut(dataList);
  }

  async clearDrivers() {
    return await this.driversTable.clear();
  }

  getDrivers(): Promise<Driver[]> {
    return this.driversTable
      .toArray();
  }

  getDriversCount(): Promise<number> {
    return this.driversTable.count();
  }

  async addShippingReportMaterials(dataList: ShippingReportMaterialDto[]) {
    return await this.shippingReportMaterialTable.bulkPut(dataList);
  }

  async clearShippingReportMaterials() {
    return await this.shippingReportMaterialTable.clear();
  }

  getShippingReportMaterial(dispatchNumber: string): Promise<ShippingReportMaterialDto[]> {
    return this.shippingReportMaterialTable.where(dispatchNumber)
      .equals(dispatchNumber)
      .toArray();
  }

  getShippingReportMaterialCount(): Promise<number> {
    return this.shippingReportMaterialTable.count();
  }

  async addShippingReportQualities(dataList: ShippingReportQualityDto[]) {
    return await this.shippingReportQualitiesTable.bulkPut(dataList);
  }

  async clearShippingReportQualities() {
    return await this.shippingReportQualitiesTable.clear();
  }

  getShippingReportQualities(dispatchNumber: string): Promise<ShippingReportQuality[]> {
    return this.shippingReportQualitiesTable.where('dispatchNumber')
      .equals(dispatchNumber)
      .toArray();
  }

  getShippingReportQualitiesCount(): Promise<number> {
    return this.shippingReportQualitiesTable.count();
  }

  async clearTripEvents() {
    return await this.tripEventsTable.clear();
  }

  async addTripEvents(dataList: TripEventDto[]) {
    const tripEvents = dataList.map(dto => new TripEvent(dto));
    return await this.tripEventsTable.bulkPut(tripEvents);
  }

  async getTripEvents(tripNumber: string, journeyId: string): Promise<TripEvent[]> {
    return await this.tripEventsTable
      .where('[tripId+journeyId]')
      .equals([tripNumber, journeyId])
      .toArray()
      .then(events => events.sort((a, b) => new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime()));
  }

  async addSynchronizationSession(data: SynchronizationSession) {
    return await this.synchronizationSessionsTable.add(data);
  }

  async getSynchronizationsSession(): Promise<SynchronizationSession> {
    return await this.synchronizationSessionsTable
      .orderBy('lastSync')
      .reverse()
      .first();
  }

  async addSynchronizations(dataList: Synchronization[]) {
    for (const item of dataList) {
      const existing = await this.synchronizationTable
        .where('itemType')
        .equals(item.itemType)
        .first();

      if (existing) {
        await this.synchronizationTable.update(existing.synchronizationId, item);
      } else {
        await this.synchronizationTable.add(item);
      }
    }
  }

  async getSynchronizations(): Promise<Synchronization[]> {
    return await this.synchronizationTable.toArray();
  }

  addTripJourneyRoute(data: TripJourneyRouteDto): Promise<string> {
    return this.tripJourneyRouteTable.add(data);
  }

  async getTripJourneyRoute(tripNumber: string, destinationId: number): Promise<TripJourneyRoute> {
    return await this.tripJourneyRouteTable
      .where('[tripNumber+destinationId]')
      .equals([tripNumber, destinationId])
      .first();
  }

  async clearUser() {
    return await this.userTable.clear();
  }

  async addUser(data: UserDto) {
    return await this.userTable.put(data);
  }

  async getUser(): Promise<User> {
    return await this.userTable.toCollection().first();
  }
}
